import { Box } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import InstallationSettingsForm from './InstallationSettingsForm'
import { useUserRoles } from 'src/Hooks/user'
import { useInstallationSettingsFormInitializer } from '../hooks'
import { FeedInPowerModel } from '../model'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { updateFeedInPower } from '../api/feed-in-power.api'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { FormikWithExitHandler } from 'src/Libs/form'
import { useSnackbarMessageBasedOnSoftwareVersion } from 'src/Libs/home-power-station'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'

interface Props {
  data: FeedInPowerModel
  updateData: (storageId?: string) => Promise<void>
  storageId?: string
  isReadOnlyAccess?: boolean
}

/**
 * Handles installation settings data. Includes fetching data, validating data, updating data
 */
const InstallationSettingsData: React.VFC<Props> = ({ data, updateData, storageId, isReadOnlyAccess = true }) => {
  const authFetch = useAuthFetchWithResponse()
  const { isUser } = useUserRoles()
  const { enqueueErrorStack } = useSnackbar()
  const { t } = useCustomTranslation()
  const { initialValues, validationSchema } = useInstallationSettingsFormInitializer(data)
  const [formValues, setFormValues] = useState<FeedInPowerModel>(initialValues)
  const pageName = t('framework.installation.installation')
  const accordionName = t('systemSettings.installation.settings.title')
  const isReadOnly = isUser || isReadOnlyAccess
  const { handleDisplaySnackbarMessageBasedOnSoftwareVersion } = useSnackbarMessageBasedOnSoftwareVersion()
  const { triggerAppRating } = useAppRatingContext()

  const onSubmit = useCallback(
    async (values: FeedInPowerModel) => {
      try {
        setFormValues(values)
        await updateFeedInPower(storageId, values, authFetch)
        await updateData(storageId)
        handleDisplaySnackbarMessageBasedOnSoftwareVersion({ pageName, accordionName })
        triggerAppRating()
      } catch (error) {
        enqueueErrorStack(t('common.systemSettings.api.error.failed-form-submission', { pageName, accordionName }))
        setFormValues({ ...formValues })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authFetch, storageId, formValues, handleDisplaySnackbarMessageBasedOnSoftwareVersion, triggerAppRating]
  )

  useEffect(() => {
    setFormValues(data)
  }, [data, storageId])

  return (
    <Box>
      <FormikWithExitHandler
        formKey="installation-settings-data-form"
        initialValues={formValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <InstallationSettingsForm disabled={isReadOnly} />
      </FormikWithExitHandler>
    </Box>
  )
}

export default InstallationSettingsData
